/**
 * PW-06 — how much of an MRS line is still legitimately issuable, in one place.
 *
 * The same arithmetic was written inline twice (the Post dialog's submit guard and its row renderer) and the
 * two already disagreed with the design: neither subtracted committed quantity, neither clamped at zero. A
 * headroom that can go negative reads as "owes stock", and a rule with two spellings drifts on the next edit.
 *
 * Pure by design — no React, no fetch — so it is testable directly and safe to call during render.
 */

/** The MRS-facing quantities of one picklist line. Field names mirror `PicklistItemDto`. */
export interface MrsHeadroomInput {
  /** Cached MRS demand (`mrsRequestedQuantity`), NOT this picklist's `quantityToPick`. */
  mrsRequestedQuantity: number;
  /** Cached total issued against the line from every system, NOT this picklist's `quantityPosted`. */
  mrsReleasedQuantity: number;
  quantityPicked: number;
  quantityPosted: number;
}

export interface MrsHeadroom {
  /** MRS demand. */
  req: number;
  /** Already issued against the MRS line, everywhere. */
  rel: number;
  /** This picklist's units in open batches (Pending, Claimed, Failed): they come off `ready`. */
  posting: number;
  /** Every picklist's Pending/Claimed posts on this MRS line: they come off `headroom`. */
  mrsInFlight: number;
  /** What the MRS still allows to be issued. Never negative. */
  headroom: number;
  /** What this picklist has picked and not yet posted or committed. Never negative. */
  ready: number;
  /** How far `ready` overshoots `headroom`. Zero when it does not. */
  over: number;
  /** Convenience for `over > 0`. */
  exceeds: boolean;
}

/**
 * PT-5 C3 — TWO in-flight figures, never one. They answer different questions:
 *
 * - `posting` is THIS picklist's units in open batches (Pending, Claimed and Failed, PW-01). They are no longer
 *   ready to post, so they come off `ready`.
 * - `mrsInFlight` is what open posts on EVERY picklist hold of the MRS line, Pending and Claimed only (DM-06; a
 *   Failed job elsewhere is not about to issue anything). They will soon count as released, so they come off
 *   `headroom`. This picklist's own Pending/Claimed batch is in both figures, and rightly lowers both.
 *
 * Both default to 0.
 */
export function mrsHeadroom(
  item: MrsHeadroomInput,
  { posting = 0, mrsInFlight = 0 }: { posting?: number; mrsInFlight?: number } = {},
): MrsHeadroom {
  const headroom = Math.max(item.mrsRequestedQuantity - item.mrsReleasedQuantity - mrsInFlight, 0);
  const ready = Math.max(item.quantityPicked - item.quantityPosted - posting, 0);
  const over = Math.max(ready - headroom, 0);

  return {
    req: item.mrsRequestedQuantity,
    rel: item.mrsReleasedQuantity,
    posting,
    mrsInFlight,
    headroom,
    ready,
    over,
    exceeds: over > 0,
  };
}
